import { Injectable } from '@angular/core';
import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';
import * as jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DatePipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ExportService {
  mimeTypes: any = {
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    xls: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    txt: 'text/plain;charset=utf-16',
    csv: 'text/csv;charset=utf-16',
    xml: 'application/xml',
    zip: 'application/zip',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    mp4: 'video/mp4',
    avi: 'video/avi',
    bmp: 'image/bmp',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ppt: 'application/vnd.ms-powerpoint',
    pps: 'application/vnd.ms-powerpoint',
    pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    pdf: 'application/pdf',
    rar: 'application/vnd.rar',
    webp: 'image/webp',
    '7z': 'application/x-7z-compressed',
    gif: 'image/gif',
    gz: 'application/gzip',
    json: 'application/json'
  };

  constructor(private datepipe: DatePipe, private translateService: TranslateService) {}

  public exportExcel(jsonData: any[], fileName: string): void {
    if (jsonData.length > 0) {
      const header = Object.keys(jsonData[0]); // columns name

      const wscols = [];
      for (const head of header) {
        wscols.push({ wch: head.length + 5 });
      }

      const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData);
      ws['!cols'] = wscols;

      const wb: XLSX.WorkBook = { Sheets: { data: ws }, SheetNames: ['data'] };
      const excelBuffer: any = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      this.saveFile(excelBuffer, fileName + '.xlsx', 'xlsx');
    }
  }

  public exportExcelWithFreq(jsonData: any[], fileName: string, reportname: string, toDate: any, fromDate: any): void {
    const currentDate = this.datepipe.transform(new Date(), 'dd-MM-yyyy h:mm:s');
    const reportGeneratedText = this.translateService.instant('app.lang.rooca.reportTitles.reportgeneratedtext');
    const frequency = this.translateService.instant('app.lang.rooca.reportTitles.frequency');
    const startDate = this.datepipe.transform(fromDate, 'dd-MM-yyyy');
    const endDate = this.datepipe.transform(toDate, 'dd-MM-yyyy');

    let Heading = [];
    Heading.push(Object.keys(jsonData[0]));

    let arr = [];
    jsonData.forEach((ticketItem: any, index: number) => {
      arr.push(Object.values(ticketItem));
    });

    //Had to create a new workbook and then add the header
    const wb = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
    const merge = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 8 } }
    ];
    ws['!merges'] = merge;

    XLSX.utils.sheet_add_aoa(ws, [[reportname]]);
    XLSX.utils.sheet_add_aoa(ws, [[frequency + ' : ' + startDate + ' To ' + endDate]], { origin: -1 });
    XLSX.utils.sheet_add_aoa(ws, [[reportGeneratedText + ' : ' + currentDate]], { origin: -1 });
    XLSX.utils.sheet_add_aoa(ws, Heading, { origin: -1 });
    XLSX.utils.sheet_add_json(ws, arr, { origin: 'A5', skipHeader: true });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    XLSX.writeFile(wb, fileName + '.xlsx');
  }

  public exportExcelWithNoFreq(jsonData: any[], fileName: string, reportname: string): void {
    const currentDate = this.datepipe.transform(new Date(), 'dd-MM-yyyy h:mm:s');
    const reportGeneratedText = this.translateService.instant('app.lang.rooca.reportTitles.reportgeneratedtext');
    let Heading = [];
    Heading.push(Object.keys(jsonData[0]));

    let arr = [];
    jsonData.forEach((ticketItem: any, index: number) => {
      arr.push(Object.values(ticketItem));
    });

    //Had to create a new workbook and then add the header
    const wb = XLSX.utils.book_new();
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet([]);
    const merge = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } }
      // { s: { r: 2, c: 0 }, e: { r: 2, c: 8 } }
    ];
    ws['!merges'] = merge;

    XLSX.utils.sheet_add_aoa(ws, [[reportname]]);
    XLSX.utils.sheet_add_aoa(ws, [[reportGeneratedText + ': ' + currentDate]], { origin: -1 });
    XLSX.utils.sheet_add_aoa(ws, Heading, { origin: -1 });
    XLSX.utils.sheet_add_json(ws, arr, { origin: 'A4', skipHeader: true });
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

    XLSX.writeFile(wb, fileName + '.xlsx');
  }

  public exportTxt(jsonData: any[], fileName: string): void {
    if (jsonData.length > 0) {
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData, { skipHeader: false });
      const excelBuffer: any = XLSX.utils.sheet_to_csv(worksheet, { FS: ',', RS: '\n' });
      this.saveFile(excelBuffer, fileName, 'txt');
    }
  }

  public exportCsv(jsonData: any[], fileName: string): void {
    if (jsonData.length > 0) {
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(jsonData, { skipHeader: true });
      const excelBuffer: any = XLSX.utils.sheet_to_csv(worksheet, { FS: ',', RS: '\n' });
      this.saveFile(excelBuffer, fileName, 'csv');
    }
  }

  public exportPDFWithFreq(jsonData: any[], fileName: string, reportName: string, fromDate: string, toDate: string) {
    const currentDate = this.datepipe.transform(new Date(), 'dd-MM-yyyy h:mm:s');
    const startDate = this.datepipe.transform(fromDate, 'dd-MM-yyyy');
    const endDate = this.datepipe.transform(toDate, 'dd-MM-yyyy');
    const reportGeneratedText = this.translateService.instant('app.lang.rooca.reportTitles.reportgeneratedtext');
    const frequency = this.translateService.instant('app.lang.rooca.reportTitles.frequency');
    const pageFormats = {
      // Size in pt of various paper formats
      a0: [2383.94, 3370.39],
      a1: [1683.78, 2383.94],
      a2: [1190.55, 1683.78],
      a3: [841.89, 1190.55],
      a4: [595.28, 841.89],
      a5: [419.53, 595.28],
      a6: [297.64, 419.53],
      a7: [209.76, 297.64],
      a8: [147.4, 209.76],
      a9: [104.88, 147.4],
      a10: [73.7, 104.88],
      b0: [2834.65, 4008.19],
      b1: [2004.09, 2834.65],
      b2: [1417.32, 2004.09],
      b3: [1000.63, 1417.32],
      b4: [708.66, 1000.63],
      b5: [498.9, 708.66],
      b6: [354.33, 498.9],
      b7: [249.45, 354.33],
      b8: [175.75, 249.45],
      b9: [124.72, 175.75],
      b10: [87.87, 124.72],
      c0: [2599.37, 3676.54],
      c1: [1836.85, 2599.37],
      c2: [1298.27, 1836.85],
      c3: [918.43, 1298.27],
      c4: [649.13, 918.43],
      c5: [459.21, 649.13],
      c6: [323.15, 459.21],
      c7: [229.61, 323.15],
      c8: [161.57, 229.61],
      c9: [113.39, 161.57],
      c10: [79.37, 113.39],
      dl: [311.81, 623.62],
      letter: [612, 792],
      'government-letter': [576, 756],
      legal: [612, 1008],
      'junior-legal': [576, 360],
      ledger: [1224, 792],
      tabloid: [792, 1224],
      'credit-card': [153, 243]
    };
    const doc = new jsPDF('p', 'pt', pageFormats.c0); // third argument - pageFormats

    if (jsonData.length > 0) {
      const head = Object.keys(jsonData[0]);
      const body = [];

      const rowCountModNew = jsonData.map(Object.values);

      rowCountModNew.forEach(element => {
        body.push(element);
      });

      const addFooters = doc => {
        const pageCount = doc.internal.getNumberOfPages();
        for (var i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(24);
          doc
            .text('Page ' + String(i) + ' of ' + String(pageCount), 2400, 3550, null, null, 'left')
            .setFont(undefined, 'bold');
        }
      };
      const header = function(data: any) {
        doc.setFontSize(48);
        doc.setTextColor(40);
        const width = doc.internal.pageSize.getWidth();
        doc.text(reportName, width / 2, 80, { align: 'center' }).setFont(undefined, 'bold');
        doc.setFontSize(24);
        doc.setR2L(false);
        doc.text(reportGeneratedText + ' : ' + currentDate, 2040, 140, null, null, 'left').setFont(undefined, 'bold');
        if (fromDate != '' && toDate != '') {
          doc
            .text(frequency + ': ' + startDate + ' To ' + endDate, 50, 140, null, null, 'left')
            .setFont(undefined, 'bold');
        }
      };
      // if(reportName === this.translateService.instant('app.lang.rooca.reportTitles.incidentreporttitle')){
      //   doc.autoTable({
      //     margin: { top: 180, bottom: 200 },
      //     tableWidth: 'auto', // 'auto'|'wrap'|number = 'auto',
      //     head: [head],
      //     body,
      //     didDrawPage : header,
      //     // Default for all columns
      //     styles: { overflow: 'linebreak', cellWidth: 'auto', minCellHeight: 'wrap', lineWidth: 1, fontSize: 20  },
      //     columnStyles: {
      //       0: {cellWidth: 140},
      //       1: {cellWidth: 120},
      //       2: {cellWidth: 700},
      //       3: {cellWidth: 200},
      //       4: {cellWidth: 150},
      //       5: {cellWidth: 130},
      //       6: {cellWidth: 200},
      //       7: {cellWidth: 80},
      //       8: {cellWidth: 130},
      //       9: {cellWidth: 100},
      //       10: {cellWidth:200},
      //       11: {cellWidth:190},
      //     },
      //     theme: 'grid', // theme: 'striped'|'grid'|'plain'|'css' = 'striped'
      //     headStyles: {
      //       fillColor: '#402250',cellWidth: 'wrap',overflow: 'linebreak'
      //     },
      //     didParseCell(cellObj: any) {
      //       cellObj.cell.styles.cellPadding = { vertical: 10 };
      //     },
      //     rowHeight: 'wrap'
      //   });
      // }
      doc.autoTable({
        margin: { top: 180, bottom: 200 },
        tableWidth: 'auto', // 'auto'|'wrap'|number = 'auto',
        head: [head],
        body,
        didDrawPage: header,
        // Default for all columns
        styles: { overflow: 'linebreak', cellWidth: 'auto', minCellHeight: 'wrap', lineWidth: 1, fontSize: 20 },
        columnStyles: {},
        theme: 'grid', // theme: 'striped'|'grid'|'plain'|'css' = 'striped'
        headStyles: {
          fillColor: '#402250',
          cellWidth: 'wrap',
          overflow: 'linebreak'
        },
        didParseCell(cellObj: any) {
          cellObj.cell.styles.cellPadding = { vertical: 10 };
        },
        rowHeight: 'wrap'
      });
      addFooters(doc);
      doc.save(fileName + '.pdf');
    }
  }
  // Refer https://github.com/simonbengtsson/jsPDF-AutoTable for documentation
  public exportPDF(jsonData: any[], fileName: string) {
    let columnWid;
    let tblWid;
    if (fileName == 'automation-log') {
      columnWid = { 2: { cellWidth: 400 } }; // Message
      tblWid = 'wrap';
    } else {
      columnWid = {};
      tblWid = 'auto';
    }
    const pageFormats = {
      // Size in pt of various paper formats
      a0: [2383.94, 3370.39],
      a1: [1683.78, 2383.94],
      a2: [1190.55, 1683.78],
      a3: [841.89, 1190.55],
      a4: [595.28, 841.89],
      a5: [419.53, 595.28],
      a6: [297.64, 419.53],
      a7: [209.76, 297.64],
      a8: [147.4, 209.76],
      a9: [104.88, 147.4],
      a10: [73.7, 104.88],
      b0: [2834.65, 4008.19],
      b1: [2004.09, 2834.65],
      b2: [1417.32, 2004.09],
      b3: [1000.63, 1417.32],
      b4: [708.66, 1000.63],
      b5: [498.9, 708.66],
      b6: [354.33, 498.9],
      b7: [249.45, 354.33],
      b8: [175.75, 249.45],
      b9: [124.72, 175.75],
      b10: [87.87, 124.72],
      c0: [2599.37, 3676.54],
      c1: [1836.85, 2599.37],
      c2: [1298.27, 1836.85],
      c3: [918.43, 1298.27],
      c4: [649.13, 918.43],
      c5: [459.21, 649.13],
      c6: [323.15, 459.21],
      c7: [229.61, 323.15],
      c8: [161.57, 229.61],
      c9: [113.39, 161.57],
      c10: [79.37, 113.39],
      dl: [311.81, 623.62],
      letter: [612, 792],
      'government-letter': [576, 756],
      legal: [612, 1008],
      'junior-legal': [576, 360],
      ledger: [1224, 792],
      tabloid: [792, 1224],
      'credit-card': [153, 243]
    };
    const doc = new jsPDF('p', 'pt', pageFormats.c0); // third argument - pageFormats

    if (jsonData.length > 0) {
      const head = Object.keys(jsonData[0]);
      const body = [];

      const rowCountModNew = jsonData.map(Object.values);

      rowCountModNew.forEach(element => {
        body.push(element);
      });

      console.log(head);

      doc.autoTable({
        tableWidth: tblWid, // 'auto'|'wrap'|number = 'auto',
        head: [head],
        body,
        // Default for all columns
        styles: { overflow: 'linebreak', cellWidth: 'auto', minCellHeight: 'wrap', lineWidth: 1 },
        columnStyles: columnWid,
        theme: 'grid', // theme: 'striped'|'grid'|'plain'|'css' = 'striped'
        headStyles: {
          fillColor: '#402250'
        },
        didParseCell(cellObj: any) {
          cellObj.cell.styles.cellPadding = { vertical: 10 };
        },
        rowHeight: 'wrap'
      });
      doc.save(fileName + '.pdf');
    }
  }
  public saveFile(buffer: any, fileName: string, fileExtension: string): void {
    console.log(fileName);
    console.log(fileExtension);
    console.log(buffer);
    const data: Blob = new Blob([buffer], { type: this.getMimeType(fileExtension) });
    FileSaver.saveAs(data, fileName);
  }

  public saveFileBase64(base64String: string, fileName: string, fileExtension: string) {
    if (base64String.indexOf(',') === -1) {
      base64String = 'data:' + this.getMimeType(fileExtension) + ';base64,' + base64String;
    }
    console.log(this.getMimeType(fileExtension));
    const byteString = atob(base64String.split(',')[1]);
    // separate out the mime component
    const mimeString = base64String
      .split(',')[0]
      .split(':')[1]
      .split(';')[0];

    // write the bytes of the string to an ArrayBuffer
    const ab = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    const ia = new Uint8Array(ab);

    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    this.saveFile(ab, fileName, fileExtension);
  }

  private getMimeType(fileExtension: string) {
    return this.mimeTypes[fileExtension.toLowerCase().trim()];
  }
}
